










import { Vue, Component, Watch } from "vue-property-decorator";

// export type TranslateDomainType = 'gen' | 'spd'; // TODO Future
const mainConfig = {
  service: "etrans", // eTranslation widget definition
  user: "Skillgo.io", // User of the translator module, used for statistical purposes only
  dynamicrefresh: true, // Prevenet refreshing the page after translation is ready
  dynamic: true, // Automatically translate new content if changed on page
  domain: "gen", // Translation domain; gen: General; spd: EU formal language
  hidden: false, // fix to override iframe hiding
  languages: {
    source: "en" // Source language is forced to English; if not defined, defaults to page lang as source
  },
  include: "#menu, .main-container, .dashboard, .modal-wrapper, .modal-wrapper-content, .gallery-modal-component", // Translate main content plus modal/gallery overlays
  config: {
    live: true, // All pages visited by user will be automatically translated to the selected language
    mode: "sml", // Force default design
    targets: {
      requester: "etranslate-requester", // Override original render target for eTrans button
      receiver: "etranslate-receiver" // Override original render target for eTrans floating message box
    }
  },
  renderAs: {
    link: false,
    icon: false,
    button: true // Rendered as a button only.
  }
};

const MINIMIZE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m14 10 7-7"/><path d="M20 10h-6V4"/><path d="m3 21 7-7"/><path d="M4 14h6v6"/></svg>';

const MAXIMIZE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="m21 3-7 7"/><path d="m3 21 7-7"/><path d="M9 21H3v-6"/></svg>';

const iframeConfig = {
  ...mainConfig,
  renderAs: {
    button: false
  }
};

@Component
export default class EtranslateComponent extends Vue {
  private isTranslated: boolean = false;
  public targetLanguage: string = "";
  private eTrans: any = null;
  private mainConfigScript: HTMLScriptElement | null = null;
  private observer: MutationObserver | null = null;
  private iframeListeners: Map<HTMLIFrameElement, EventListener> = new Map();
  // Keyed on Document (not the iframe element) so that iframe navigation —
  // which replaces contentDocument — correctly triggers re-injection.
  private processedDocs: WeakSet<Document> = new WeakSet();
  private isInIframe: boolean = false;
  private targetObserver: MutationObserver | null = null;
  // Tracks how many iframe etrans scripts are still loading in SCORM mode.
  private pendingIframeScripts: number = 0;
  // Ensures the automatic SCORM re-translate fires at most once.
  private firstScormTranslationRetried: boolean = false;
  // Notification popup minimize/maximize state.
  private isPopupMinimized: boolean = false;
  private notificationObserver: MutationObserver | null = null;
  private patchedNotifications: WeakSet<Element> = new WeakSet();
  private isPatchingPopup: boolean = false;

  mounted() {
    this.isInIframe = window.self !== window.top;

    this.mainConfigScript = document.createElement("script");
    this.mainConfigScript.type = "application/json";
    this.mainConfigScript.className = "etranslate-script";
    this.mainConfigScript.textContent = JSON.stringify(mainConfig);

    if (this.isInIframe) {
      // SCORM/iframe: the $wt library detects isFrame and strips config.targets,
      // config.mode, and hidden — breaking rendering completely.
      // Strategy: load the SML first, lock $wt.isFrame to false via
      // Object.defineProperty, THEN inject config into #etranslate-container.
      // The widget processes it in "normal" mode, preserving targets/mode/hidden.
      this.initForIframe();
    } else {
      // Normal: inject config immediately, then load SML
      this.appendConfigScript(
        document,
        this.mainConfigScript,
        "etranslate-container"
      );
      this.loadSmartLoaderScript();
    }

    const observer = new MutationObserver(() => {
      const iframes = document.querySelectorAll("iframe");
      this.injectEtranslationIntoIframes(iframes);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    this.observer = observer;

    // Scan iframes already present in the DOM at mount time —
    // the MutationObserver only fires on future changes.
    this.injectEtranslationIntoIframes(document.querySelectorAll("iframe"));

    window.addEventListener("wtEtransReady", this.handleEtransReady);
    window.addEventListener("wtTranslationStart", this.handleTranslationStart);
    window.addEventListener("wtTranslationEnd", this.handleTranslationEnd);
    window.addEventListener("wtTranslationAbort", this.handleTranslationAbort);
    window.addEventListener("wtTranslationError", this.handleTranslationError);

    // Watch for the widget's notification popup appearing so we can patch it.
    this.notificationObserver = new MutationObserver(() => {
      if (!this.isPatchingPopup) {
        this.isPatchingPopup = true;
        this.patchNotificationPopup();
        // Clear after microtask chain settles — MutationObserver callbacks are
        // microtasks, so setTimeout (macrotask) runs after them.
        setTimeout(() => { this.isPatchingPopup = false; }, 0);
      }
    });
    const receiver = document.getElementById("etranslate-receiver");
    if (receiver) {
      this.notificationObserver.observe(receiver, { childList: true, subtree: true });
    }

    // Fallback observer: if the widget still restructures DOM, recreate target elements
    if (this.isInIframe) {
      this.targetObserver = new MutationObserver(() => {
        this.$nextTick(() => this.ensureTargetElements());
      });
      this.targetObserver.observe(this.$el, { childList: true });
    }
  }

  beforeDestroy() {
    window.removeEventListener("wtEtransReady", this.handleEtransReady);
    window.removeEventListener(
      "wtTranslationStart",
      this.handleTranslationStart
    );
    window.removeEventListener("wtTranslationEnd", this.handleTranslationEnd);
    window.removeEventListener(
      "wtTranslationAbort",
      this.handleTranslationAbort
    );
    window.removeEventListener(
      "wtTranslationError",
      this.handleTranslationError
    );

    if (this.observer) {
      this.observer.disconnect();
    }

    this.iframeListeners.forEach((listener, iframe) => {
      iframe.removeEventListener("load", listener);
    });
    this.iframeListeners.clear();
    // processedDocs is a WeakSet — no manual clear needed.

    if (this.targetObserver) {
      this.targetObserver.disconnect();
    }

    if (this.notificationObserver) {
      this.notificationObserver.disconnect();
    }
  }

  @Watch("$route.path")
  private() {
    setTimeout(() => {
      if (this.eTrans) {
        this.eTrans.abortTranslation();
        setTimeout(() => {
          this.autoTranslatePage();
          // Patch the warning popup that appears on navigation re-translation.
          setTimeout(() => this.patchNotificationPopup(), 200);
        }, 500);
      }
    }, 500);
  }

  appendConfigScript(
    document: Document,
    configScript: HTMLScriptElement,
    containerId: string
  ) {
    const container = document.getElementById(containerId);

    if (container) {
      container.appendChild(configScript);
    } else {
      setTimeout(() => {
        this.appendConfigScript(document, configScript, containerId);
      }, 100);
    }
  }

  getExistingSmartLoaderScript(document: Document): Element | null {
    return document.querySelector(
      "script[src^='https://webtools.europa.eu/load.js']"
    );
  }

  autoTranslatePage() {
    if (this.isTranslated) {
      this.translatePage();
    }
  }

  translatePage() {
    this.eTrans.translate(
      "#menu, .main-container, .dashboard",
      this.targetLanguage
    );
  }

  // Hide the text appearing next to the Translate button
  hideEtransText() {
    const container: HTMLElement | null = document.getElementById(
      "etranslate-container"
    );
    if (container) {
      const spans = container.querySelectorAll("span");
      spans.forEach(span => {
        span.style.display = "none";
      });
    }
  }

  handleEtransReady() {
    const etransButton = document.getElementById("eTransButton");
    if (etransButton) {
      etransButton.classList.remove("wt-ecl-button", "wt-ecl-button--primary");
      etransButton.classList.add("etransbutton", "button", "green");
      etransButton.style.width = "110px";
      etransButton.style.fontFamily = "Squad, Helvetica, sans-serif";
    }

    if (this.isInIframe) {
      this.ensureTargetElements();
      this.forceWidgetFrameVisible();
    }
  }

  handleTranslationStart(e: Event): void {
    const target = e.target as any;
    this.eTrans = target.$wt?.etrans;
    setTimeout(() => {
      this.hideAllEtransIframeWidget();
    }, 100);

    // Patch the popup after the widget renders it (short delay).
    setTimeout(() => this.patchNotificationPopup(), 200);

    if (this.isInIframe) {
      this.ensureTargetElements();
    }
  }

  handleTranslationEnd(e: Event): void {
    const target = e.target as any;
    this.targetLanguage = target.$wt?.etrans.toLanguage;
    this.isTranslated = true;
    this.hideEtransText();

    // Re-patch: the widget may replace the popup DOM on translation end.
    setTimeout(() => this.patchNotificationPopup(), 200);
  }

  handleTranslationAbort(e: Event): void {
  }

  handleTranslationError(e: Event): void {
  }

  returnToOriginal(): void {
    if (this.eTrans) {
      this.eTrans.abortTranslation();
    }
    this.isTranslated = false;
  }

  /**
   * SCORM/iframe-safe "return to original language".
   *
   * Uses the widget's own resetState() API to restore the translate button
   * and hide the receiver popup, then route-bounces Vue so every component
   * re-renders from reactive data (overwriting the widget's DOM text edits).
   */
  private returnToOriginalInIframe(): void {
    const wt = (window as any).$wt;

    // 1. Abort any in-flight translation.
    if (this.eTrans) {
      this.eTrans.abortTranslation();
    }

    // 2. Remove the live-translation cookie so it doesn't auto-restart.
    if (wt?.etrans?.removeLiveCookie) {
      wt.etrans.removeLiveCookie();
    }

    // 3. Use the widget's own resetState() — this re-creates the translate
    //    button (requester.setStateWaiting + handleEvents), hides the
    //    receiver popup, and re-injects the modal.
    if (wt?.etrans?.resetState) {
      wt.etrans.resetState();
    }

    // 4. Work around bugs in the widget's resetState(): it writes
    //    $wt.etranstranslating / $wt.etranstranslated (missing dots)
    //    instead of $wt.etrans.translating / $wt.etrans.translated,
    //    so the flags stay true and the widget re-enters the "already
    //    translated" branch on the next run() call.
    if (wt?.etrans) {
      wt.etrans.translated = false;
      wt.etrans.translating = false;
      wt.etrans.toLanguage = null;
    }

    // 4b. Restore the HTML lang attribute to the source language.
    //     The widget set it to the target language during translation,
    //     and its UI labels (snippetLang → $wt.lang → document.lang)
    //     render in whatever language this attribute holds.
    document.documentElement.lang = "en";

    // 4c. Clear the widget's localStorage translation cache so
    //     isTranslated() won't find a cached translation on re-run.
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("wtEtrans-")) {
          localStorage.removeItem(key);
        }
      });
    } catch { /* localStorage may not be available in some iframes */ }

    // 5. Reset our component state.
    this.isTranslated = false;
    this.targetLanguage = "";
    this.eTrans = null;
    this.patchedNotifications = new WeakSet();

    // 6. Clear any remaining notification popups.
    const receiver = document.getElementById("etranslate-receiver");
    if (receiver) {
      receiver.innerHTML = "";
    }

    // 7. Route-bounce: navigate away and back to force Vue to re-render
    //    the current route from reactive data, overwriting the widget's
    //    DOM text changes with the original English content.
    const currentRoute = this.$route.fullPath;
    this.$router.replace("/").then(() => {
      this.$nextTick(() => {
        this.$router.replace(currentRoute).then(() => {
          this.$nextTick(() => {
            this.ensureTargetElements();
            this.forceWidgetFrameVisible();

            // Style the button once the widget has re-rendered it.
            this.styleTranslateButton();

            // Force the header to remount — it's outside <router-view>
            // so the route-bounce doesn't re-render it, but the widget
            // modified its DOM text during translation.
            this.$root.$emit('remount-header');

            // Final cleanup: remove any late notification popups.
            setTimeout(() => {
              document.querySelectorAll(
                ".wt-ecl-notification.wt-ecl-notification--info, .wt-ecl-notification.wt-ecl-notification--warning"
              ).forEach(el => el.remove());
              const recv = document.getElementById("etranslate-receiver");
              if (recv) {
                recv.innerHTML = "";
              }
            }, 1000);
          });
        });
      });
    });
  }

  /** Apply our custom styling to the widget's native translate button. */
  private styleTranslateButton(): void {
    let attempts = 0;
    const poll = () => {
      const btn = document.getElementById("eTransButton");
      if (btn) {
        btn.classList.remove("wt-ecl-button", "wt-ecl-button--primary");
        btn.classList.add("etransbutton", "button", "green");
        btn.style.width = "110px";
        btn.style.fontFamily = "Squad, Helvetica, sans-serif";
        this.hideEtransText();
      } else if (attempts < 25) {
        attempts++;
        setTimeout(poll, 200);
      }
    };
    setTimeout(poll, 300);
  }

  // --- SCORM / iframe compatibility ---

  /**
   * In SCORM/iframe mode: load the SML script first (without any config in
   * the DOM).  After it loads, lock $wt.isFrame to false with
   * Object.defineProperty, then inject the config into #etranslate-container.
   * The SPA MutationObserver detects the new script and processes it with
   * isFrame === false, preserving config.targets/mode/hidden.
   */
  private initForIframe(): void {
    const existingScript = this.getExistingSmartLoaderScript(document);
    if (existingScript) {
      this.overrideIsFrameAndInjectConfig();
    } else {
      const script = document.createElement("script");
      script.src = "https://webtools.europa.eu/load.js?spa=1";
      script.defer = true;
      script.type = "text/javascript";
      script.addEventListener("load", () => {
        this.overrideIsFrameAndInjectConfig();
      });
      document.head.appendChild(script);
    }
  }

  /** Load the SML script in the normal (non-iframe) flow. */
  private loadSmartLoaderScript(): void {
    const scriptAlreadyExists = this.getExistingSmartLoaderScript(document);
    if (!scriptAlreadyExists) {
      const script = document.createElement("script");
      script.src = "https://webtools.europa.eu/load.js?spa=1";
      script.defer = true;
      script.type = "text/javascript";
      document.head.appendChild(script);
    }
  }

  /**
   * Poll for $wt, lock isFrame to false, then inject config into
   * #etranslate-container.  Object.defineProperty with a getter/setter
   * ensures the value stays false even if the widget re-checks or re-assigns.
   */
  private overrideIsFrameAndInjectConfig(): void {
    const poll = () => {
      const wt = (window as any).$wt;
      if (wt) {
        try {
          Object.defineProperty(wt, "isFrame", {
            get: () => false,
            set: () => { /* silently discard writes */ },
            configurable: true
          });
        } catch {
          // Fallback if property is non-configurable (shouldn't happen)
          wt.isFrame = false;
        }
        // Now inject config into the visible container.
        // The SPA MutationObserver will pick it up and process it
        // with isFrame locked to false.
        this.appendConfigScript(
          document,
          this.mainConfigScript!,
          "etranslate-container"
        );
      } else {
        setTimeout(poll, 50);
      }
    };
    poll();
  }

  /** Recreate .etranslate-target if the widget removed it from Vue's DOM. */
  ensureTargetElements(): void {
    const wrapper = this.$el as HTMLElement;
    if (!wrapper) return;

    let targetDiv = wrapper.querySelector(".etranslate-target") as HTMLElement;
    if (!targetDiv) {
      targetDiv = document.createElement("div");
      targetDiv.className = "etranslate-target";
      wrapper.appendChild(targetDiv);
    }

    if (!targetDiv.querySelector("#etranslate-requester")) {
      const requester = document.createElement("div");
      requester.id = "etranslate-requester";
      requester.className = "etranslate-requester";
      targetDiv.insertBefore(requester, targetDiv.firstChild);
    }

    if (!targetDiv.querySelector("#etranslate-receiver")) {
      const receiver = document.createElement("div");
      receiver.id = "etranslate-receiver";
      receiver.className = "etranslate-receiver";
      targetDiv.appendChild(receiver);
    }
  }

  /** Force widget frame visible (widget hides it when isFrame was true). */
  forceWidgetFrameVisible(): void {
    const frames = document.querySelectorAll(
      ".wt-etrans-frame, .wt-etrans-frame-spacer"
    );
    frames.forEach((frame: Element) => {
      (frame as HTMLElement).style.removeProperty("display");
    });
  }

  // --- Handling iFrames within the translated page ---

  injectEtranslationIntoIframes(iframes: NodeListOf<HTMLIFrameElement>) {
    iframes.forEach((iframe, index) => {
      // Attach the load listener BEFORE checking readyState to close the
      // TOCTOU race: if the iframe finishes loading between the readyState
      // check and addEventListener, both the listener and the immediate
      // call below will fire — the processedDocs guard prevents double-injection.
      if (!this.iframeListeners.has(iframe)) {
        const loadListener = () => {
          this.injectEtranslationIntoIframe(iframe.contentDocument, index);
        };
        iframe.addEventListener("load", loadListener);
        this.iframeListeners.set(iframe, loadListener);
      }

      // If already loaded, inject immediately (listener won't re-fire).
      if (iframe?.contentDocument?.readyState === "complete") {
        this.injectEtranslationIntoIframe(iframe.contentDocument, index);
      }
    });
  }

  injectEtranslationIntoIframe(doc: Document | null, index: number) {
    if (!doc) return;

    // Guard keyed on the Document object, not the iframe element, so that
    // iframe navigation (which replaces contentDocument) triggers re-injection
    // while same-document double-calls (TOCTOU) are still blocked.
    if (this.processedDocs.has(doc)) return;

    try {
      const scriptAlreadyExists = this.getExistingSmartLoaderScript(doc);
      if (scriptAlreadyExists) return;

      this.processedDocs.add(doc);

      const iframeConfigScript = doc.createElement("script");
      iframeConfigScript.type = "application/json";
      iframeConfigScript.className = "etranslate-script";
      iframeConfigScript.textContent = JSON.stringify(iframeConfig);

      const containerId = `etranslate-container-${index}`;
      this.iFrameAppendEtransContainer(doc, containerId);
      this.appendConfigScript(doc, iframeConfigScript, containerId);
      this.iFrameAppendEtransScript(doc);
    } catch (e) {
      // Cross-origin iframe — contentDocument access throws SecurityError.
      console.log(
        "eTranslation cannot access iframe contentDocument due to cross-origin restrictions:",
        e
      );
    }
  }

  iFrameAppendEtransContainer(doc: Document, containerId: string) {
    const container = doc.createElement("div");
    container.id = containerId;
    container.style.display = "none";

    const target = doc.createElement("div");
    target.classList.add("etranslate-target");

    const requester = doc.createElement("div");
    requester.id = "etranslate-requester";
    requester.classList.add("etranslate-requester");

    const receiver = doc.createElement("div");
    receiver.id = "etranslate-receiver";
    receiver.classList.add("etranslate-receiver");

    target.appendChild(requester);
    target.appendChild(receiver);
    container.appendChild(target);
    doc.body.appendChild(container);
  }

  iFrameAppendEtransScript(doc: Document) {
    const script = doc.createElement("script");
    script.src = "https://webtools.europa.eu/load.js?spa=1";
    script.defer = true;
    script.type = "text/javascript";

    // In SCORM mode the page itself is an iframe, so sub-iframe scripts may
    // finish loading after the first translation runs.  Track each injection
    // and re-translate once all scripts are ready if needed.
    if (this.isInIframe) {
      this.pendingIframeScripts++;
      const onSettled = () => {
        script.removeEventListener("load", onSettled);
        script.removeEventListener("error", onSettled);
        this.pendingIframeScripts--;
        this.onIframeScriptLoaded();
      };
      script.addEventListener("load", onSettled);
      script.addEventListener("error", onSettled);
    }

    doc.head.appendChild(script);
  }

  /**
   * Called each time an injected iframe etrans script settles (load/error).
   * If all scripts are now done and translation already completed, trigger
   * a single automatic re-translate so the iframe content is picked up.
   */
  private onIframeScriptLoaded(): void {
    if (
      this.isInIframe &&
      !this.firstScormTranslationRetried &&
      this.pendingIframeScripts === 0 &&
      this.isTranslated &&
      this.eTrans
    ) {
      this.firstScormTranslationRetried = true;
      // Give the newly-loaded widget a moment to initialise before translating.
      setTimeout(() => this.translatePage(), 500);
    }
  }

  // --- Notification popup minimize/maximize ---

  /**
   * Find the widget's notification popup and patch it:
   * - Replace the four-arrow SVG with minimize/maximize toggle.
   * - Apply the current minimized/maximized state.
   */
  private patchNotificationPopup(): void {
    const popups = document.querySelectorAll(
      ".wt-ecl-notification.wt-ecl-notification--info, .wt-ecl-notification.wt-ecl-notification--warning"
    );
    popups.forEach(el => this.patchSinglePopup(el as HTMLElement));
  }

  private patchSinglePopup(popup: HTMLElement): void {
    if (!popup) return;
    // Don't patch the same DOM element twice.
    if (this.patchedNotifications.has(popup)) {
      // Even if already patched, reapply current state (widget may have reset styles).
      if (this.isPopupMinimized) {
        this.applyMinimizedState(popup);
      } else {
        this.applyMaximizedState(popup);
      }
      return;
    }
    this.patchedNotifications.add(popup);

    // Force survey links to open in a new tab so they don't navigate away.
    // Exclude the "return to original language" link — it must stay in-page.
    popup.querySelectorAll('a[href]:not(#wtEtransReturnToLanguage)').forEach(a => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });

    // In iframe/SCORM mode the widget's "return to original language" link
    // navigates the iframe away (blank screen / redirects to /dashboard).
    // Instead: abort translation, reset state, and force Vue to re-render
    // the current route — this restores the original DOM text from Vue's
    // reactive data without a full page reload.
    if (this.isInIframe) {
      const returnLink = popup.querySelector('#wtEtransReturnToLanguage') as HTMLAnchorElement;
      if (returnLink) {
        returnLink.addEventListener('click', (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          this.returnToOriginalInIframe();
        });
      }
    }

    // Find and remove the original four-arrow SVG.
    const fourArrowSvg = popup.querySelector('svg.wt-icon--s');
    if (fourArrowSvg) {
      fourArrowSvg.remove();
    }

    // Create the toggle button container.
    const toggleBtn = document.createElement("button");
    toggleBtn.type = "button";
    toggleBtn.className = "etranslate-popup-toggle";
    toggleBtn.innerHTML = this.isPopupMinimized ? MAXIMIZE_SVG : MINIMIZE_SVG;
    toggleBtn.addEventListener("click", () => {
      this.isPopupMinimized = !this.isPopupMinimized;
      if (this.isPopupMinimized) {
        this.applyMinimizedState(popup);
      } else {
        this.applyMaximizedState(popup);
      }
    });

    popup.appendChild(toggleBtn);

    // Apply initial state.
    if (this.isPopupMinimized) {
      this.applyMinimizedState(popup);
    } else {
      this.applyMaximizedState(popup);
    }
  }

  private applyMinimizedState(popup: HTMLElement): void {
    popup.classList.add("etranslate-popup-minimized");

    // Hide all children except the left icon and the toggle button.
    Array.from(popup.children).forEach(child => {
      const el = child as HTMLElement;
      if (
        el.classList.contains("wt-ecl-notification__icon") ||
        el.classList.contains("etranslate-popup-toggle")
      ) {
        el.style.display = "";
      } else {
        el.style.display = "none";
      }
    });

    // Update toggle icon to maximize.
    const toggleBtn = popup.querySelector(".etranslate-popup-toggle");
    if (toggleBtn) {
      toggleBtn.innerHTML = MAXIMIZE_SVG;
    }
  }

  private applyMaximizedState(popup: HTMLElement): void {
    popup.classList.remove("etranslate-popup-minimized");

    // Show all children.
    Array.from(popup.children).forEach(child => {
      (child as HTMLElement).style.display = "";
    });

    // Hide the four-arrow SVG — the widget may re-inject it.
    const fourArrowSvg = popup.querySelector('svg.wt-icon--s');
    if (fourArrowSvg) {
      (fourArrowSvg as HTMLElement).style.display = "none";
    }

    // Update toggle icon to minimize.
    const toggleBtn = popup.querySelector(".etranslate-popup-toggle");
    if (toggleBtn) {
      toggleBtn.innerHTML = MINIMIZE_SVG;
    }
  }

  hideEtransIframeWidgets(doc: Document | null) {
    if (doc) {
      const iframeEtrans = doc.getElementsByClassName(
        "wt-etrans-frame"
      )[0] as HTMLElement;
      const iframeEtransSpacer = doc.getElementsByClassName(
        "wt-etrans-frame-spacer"
      )[0] as HTMLElement;
      if (iframeEtrans && !(iframeEtrans.style.display === "none"))
        iframeEtrans.style.display = "none";
      if (iframeEtransSpacer && !(iframeEtransSpacer.style.display === "none"))
        iframeEtransSpacer.style.display = "none";
    }
  }

  hideAllEtransIframeWidget() {
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach(iframe => {
      this.hideEtransIframeWidgets(iframe.contentDocument);
    });
  }
}
